// cs2586 (INC-0280, 22/10/2026): TITLE da listagem por CONTEXTO. O title de toda listagem de uma cidade com
// SEO cadastrado no CRM (tbl_seo_cidade) era o título da CIDADE: bairro, condomínio e tipo de Porto Alegre
// saíam todos com o mesmo "<título da cidade>" (a description/og/canonical já eram específicos).
// Sob o gate:
//   - URL SÓ da cidade (bairro, empreendimento e tipo = "-") → título da cidade, como hoje;
//   - com condomínio/empreendimento → meta_title do empreendimento (Gestão › Empreendimentos › Descrições)
//     e, sem ele, o título da busca (buildImoveisSeo); description = meta_description quando preenchida;
//   - com bairro ou tipo → o título da busca (buildImoveisSeo).
// O texto e o FAQ da cidade no rodapé NÃO mudam (o cliente pediu preservar). Fora do gate: zero diferença
// (quem chama nem consulta estas funções). Molde: utils/topoHomeNasInternas.ts.
// cs2606 (23/10/2026, Marcello & Oliveira): mesmo pedido — o title de Jundiaí vazava para bairro/tipo.
// cs2504 (30/10/2026, Sua Casa Digital): idem — "Imóveis de alto padrão… no Rio de Janeiro" em Ipanema, Leblon etc.
export const SEO_CONTEXTO_LISTAGEM = new Set<number>([2586, 2606, 2504]);

export function temSeoContextoListagem(site: any): boolean {
    return SEO_CONTEXTO_LISTAGEM.has(Number(site?.id));
}

/** Empreendimento da URL resolvido no SSR (só os campos de SEO + o segmento a que ele pertence). */
export type EmpreendimentoSeoListagem = {
    segmento: string;
    meta_title?: string | null;
    meta_description?: string | null;
};

export type SegmentosListagem = { cidade: string; bairro: string; empreendimento: string; tipo: string };

const chaveSegmento = (segmento: unknown): string => {
    let valor = String(segmento ?? '').trim();
    try {
        valor = decodeURIComponent(valor);
    } catch {
        /* segmento malformado: usa cru */
    }
    valor = valor.trim().toLowerCase();
    return valor || '-';
};

/** Segmentos de /imoveis/{finalidade}/{cidade}/{bairro}/{empreendimento}/{tipo} ("-" = sem filtro). */
export function segmentosDaListagem(asPath: unknown): SegmentosListagem | null {
    const partes = String(asPath ?? '').split('#')[0].split('?')[0].split('/').filter(Boolean);
    if (partes[0] !== 'imoveis') return null;
    return {
        cidade: chaveSegmento(partes[2]),
        bairro: chaveSegmento(partes[3]),
        empreendimento: chaveSegmento(partes[4]),
        tipo: chaveSegmento(partes[5]),
    };
}

/** O empreendimento do SSR só vale enquanto a URL ainda é a dele (no cliente os filtros mudam sem SSR). */
const empreendimentoDaUrl = (
    segmentos: SegmentosListagem,
    empreendimento: EmpreendimentoSeoListagem | null | undefined
): EmpreendimentoSeoListagem | null =>
    empreendimento && segmentos.empreendimento !== '-' && chaveSegmento(empreendimento.segmento) === segmentos.empreendimento
        ? empreendimento
        : null;

const texto = (valor: unknown): string => String(valor ?? '').replace(/\s+/g, ' ').trim();

/** Title da listagem sob o gate (quem chama só usa dentro de temSeoContextoListagem). */
export function tituloListagemPorContexto(
    asPath: unknown,
    {
        tituloCidade,
        tituloBusca,
        empreendimento,
    }: {
        tituloCidade?: unknown;
        tituloBusca?: unknown;
        empreendimento?: EmpreendimentoSeoListagem | null;
    }
): string {
    const busca = texto(tituloBusca);
    const segmentos = segmentosDaListagem(asPath);
    if (!segmentos) return busca;
    const soCidade =
        segmentos.cidade !== '-' && segmentos.bairro === '-' && segmentos.empreendimento === '-' && segmentos.tipo === '-';
    if (soCidade) return texto(tituloCidade) || busca;
    if (segmentos.empreendimento !== '-') {
        return texto(empreendimentoDaUrl(segmentos, empreendimento)?.meta_title) || busca;
    }
    return busca;
}

/** Description do condomínio da URL sob o gate ('' = a de sempre). */
export function descricaoListagemPorContexto(
    asPath: unknown,
    empreendimento?: EmpreendimentoSeoListagem | null
): string {
    const segmentos = segmentosDaListagem(asPath);
    if (!segmentos) return '';
    return texto(empreendimentoDaUrl(segmentos, empreendimento)?.meta_description);
}
